<!--
  YYEVA MP4 动效容器（与 lottie-animation、svga-animation 分工）
  仅在客户端挂载后加载 yyeva（依赖 DOM / Canvas / Video）
-->
<template>
	<div ref="rootRef" class="yyeva-animation" :style="rootStyle" />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";

const props = defineProps({
	/** .mp4 资源 URL（建议 Vite `import url from '…?url'`） */
	src: {
		type: String,
		required: true,
	},
	/** 容器宽度（px 或带单位字符串） */
	width: {
		type: [Number, String],
		default: "100%",
	},
	/** 容器高度（px 或带单位字符串） */
	height: {
		type: [Number, String],
		default: "100%",
	},
	/** 为 true 时按 750 设计稿将数字 width/height 转为 vw */
	useDesignViewport: {
		type: Boolean,
		default: false,
	},
	/** 设计稿宽度，与 postcss viewportWidth 一致 */
	designViewportWidth: {
		type: Number,
		default: 750,
	},
	/** 是否循环播放，或指定循环次数 */
	loop: {
		type: [Boolean, Number],
		default: true,
	},
	/** 初始化后是否自动播放 */
	autoplay: {
		type: Boolean,
		default: true,
	},
	/** 播放完毕后是否暂停在结束帧（需外部 destroy 释放资源） */
	endPause: {
		type: Boolean,
		default: false,
	},
	/** 静音播放（浏览器自动播放策略通常要求静音） */
	mute: {
		type: Boolean,
		default: true,
	},
	/** 动态元素配置（文本、图片等，需与素材 AE 插件导出字段对应） */
	effects: {
		type: Object,
		default: undefined,
	},
	/** 微信等环境预声明 video 容器 id，避免重复授权点击 */
	videoId: {
		type: String,
		default: undefined,
	},
	/** 日志级别，生产环境建议 info */
	logLevel: {
		type: String,
		default: "info",
	},
	/**
	 * Alpha 通道位置：标准透明 MP4 为左 RGB + 右 Alpha，应传 'right'
	 * 'left' 表示 Alpha 在左侧（部分平台导出格式）
	 */
	alphaDirection: {
		type: String,
		default: "right",
		validator: value => ["left", "right"].includes(value),
	},
});

const emit = defineEmits(["ready", "error", "start", "end"]);

const rootRef = ref(null);
let player = null;

function toSizeStyle(value, axis) {
	if (typeof value === "number") {
		if (props.useDesignViewport) {
			const vw = (value / props.designViewportWidth) * 100;
			return `${vw}vw`;
		}
		return `${value}px`;
	}
	return value;
}

const rootStyle = computed(() => ({
	width: toSizeStyle(props.width, "width"),
	height: toSizeStyle(props.height, "height"),
}));

function destroyYyEva() {
	if (player) {
		try {
			player.destroy?.();
		} catch (_) {
			/* ignore */
		}
		player = null;
	}
}

async function mountYyEva() {
	if (!import.meta.client || !rootRef.value || !props.src) {
		return;
	}

	destroyYyEva();

	try {
		const { yyEva } = await import("yyeva");
		const options = {
			container: rootRef.value,
			videoUrl: props.src,
			loop: props.loop,
			autoplay: props.autoplay,
			endPause: props.endPause,
			mute: props.mute,
			logLevel: props.logLevel,
			alphaDirection: props.alphaDirection,
			// 本地同名替换 mp4 时 URL 不变，IndexedDB 会一直命中旧素材；开发环境关闭缓存
			useVideoDBCache: !import.meta.dev,
			onStart: () => emit("start"),
			onEnd: () => emit("end"),
			onError: error => emit("error", error),
		};

		if (props.effects) {
			options.effects = props.effects;
		}
		if (props.videoId) {
			options.videoID = props.videoId;
		}

		player = await yyEva(options);
		emit("ready");

		if (props.autoplay) {
			player?.start?.();
		}
	} catch (error) {
		emit("error", error);
	}
}

onMounted(() => {
	void mountYyEva();
});

onUnmounted(() => {
	destroyYyEva();
});

watch(
	() => props.src,
	() => {
		void mountYyEva();
	}
);

defineExpose({
	start: () => player?.start?.(),
	stop: () => player?.stop?.(),
	pause: () => player?.pause?.(),
	resume: () => player?.resume?.(),
	destroy: destroyYyEva,
	getPlayer: () => player,
});
</script>

<style scoped>
.yyeva-animation {
	display: block;
	line-height: 0;
	overflow: hidden;
}
</style>
